import type { MarkdownCountdownEditRequest } from './markdown-countdown';
import type { MarkdownImageEditRequest } from './markdown-image-node';
import type { MarkdownMapEditRequest } from './markdown-map';
import type { MarkdownMessageEditRequest } from './markdown-message';
import type { Editor } from '@tiptap/react';

import { Box, Button, Flex } from '@chakra-ui/react';
import { useEditorState } from '@tiptap/react';
import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import {
  closeMarkdownSlashMenu,
  createMarkdownSlashCommands,
  filterMarkdownSlashCommands,
  INACTIVE_MARKDOWN_SLASH_MENU_STATE,
  MARKDOWN_SLASH_MENU_PLUGIN_KEY,
  runMarkdownSlashCommand,
  setMarkdownSlashMenuSelectedIndex,
} from './markdown-slash-menu';

interface MarkdownSlashMenuPopoverProps {
  editor: Editor | null;
  onRequestCountdownEdit?: (request: MarkdownCountdownEditRequest) => void;
  onRequestImageEdit?: (request: MarkdownImageEditRequest) => void;
  onRequestMapEdit?: (request: MarkdownMapEditRequest) => void;
  onRequestMessageEdit?: (request: MarkdownMessageEditRequest) => void;
}

const MARKDOWN_SLASH_MENU_WIDTH = 280;
const MARKDOWN_SLASH_MENU_COLUMNS = 5;
const MARKDOWN_SLASH_MENU_BUTTON_SIZE = 44;
const MARKDOWN_SLASH_MENU_BUTTON_GAP = 8;
const MARKDOWN_SLASH_MENU_VERTICAL_PADDING = 16;
const MARKDOWN_SLASH_MENU_MAX_HEIGHT = 320;

export function MarkdownSlashMenuPopover({
  editor,
  onRequestCountdownEdit,
  onRequestImageEdit,
  onRequestMapEdit,
  onRequestMessageEdit,
}: MarkdownSlashMenuPopoverProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const slashMenuState =
    useEditorState({
      editor,
      selector: ({ editor: currentEditor }) =>
        currentEditor
          ? (MARKDOWN_SLASH_MENU_PLUGIN_KEY.getState(currentEditor.state) ??
            INACTIVE_MARKDOWN_SLASH_MENU_STATE)
          : INACTIVE_MARKDOWN_SLASH_MENU_STATE,
    }) ?? INACTIVE_MARKDOWN_SLASH_MENU_STATE;

  const commands = useMemo(
    () =>
      editor
        ? createMarkdownSlashCommands(
            editor,
            onRequestCountdownEdit,
            onRequestImageEdit,
            onRequestMapEdit,
            onRequestMessageEdit,
          )
        : [],
    [
      editor,
      onRequestCountdownEdit,
      onRequestImageEdit,
      onRequestMapEdit,
      onRequestMessageEdit,
    ],
  );
  const filteredCommands = useMemo(
    () => filterMarkdownSlashCommands(commands, slashMenuState.query),
    [commands, slashMenuState.query],
  );

  const menuHeight = useMemo(() => {
    const rows = Math.max(
      1,
      Math.ceil(filteredCommands.length / MARKDOWN_SLASH_MENU_COLUMNS),
    );
    const contentHeight =
      rows * MARKDOWN_SLASH_MENU_BUTTON_SIZE +
      Math.max(0, rows - 1) * MARKDOWN_SLASH_MENU_BUTTON_GAP +
      MARKDOWN_SLASH_MENU_VERTICAL_PADDING;
    return Math.min(contentHeight, MARKDOWN_SLASH_MENU_MAX_HEIGHT);
  }, [filteredCommands.length]);

  const position = useMemo(() => {
    if (!editor || !slashMenuState.active || !slashMenuState.range) {
      return null;
    }

    try {
      const coords = editor.view.coordsAtPos(slashMenuState.range.to);
      const pad = 8;
      const width = MARKDOWN_SLASH_MENU_WIDTH;
      return {
        left: Math.max(
          pad,
          Math.min(coords.left, window.innerWidth - width - pad),
        ),
        top: Math.max(
          pad,
          Math.min(coords.bottom + 4, window.innerHeight - menuHeight - pad),
        ),
      };
    } catch {
      return null;
    }
  }, [editor, menuHeight, slashMenuState.active, slashMenuState.range]);

  useEffect(() => {
    if (!editor || !slashMenuState.active) {
      return;
    }

    const onDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (menuRef.current?.contains(target)) {
        return;
      }

      closeMarkdownSlashMenu(editor);
    };

    document.addEventListener('mousedown', onDocumentMouseDown, true);
    return () => {
      document.removeEventListener('mousedown', onDocumentMouseDown, true);
    };
  }, [editor, slashMenuState.active]);

  useEffect(() => {
    if (!editor || !slashMenuState.active) {
      return;
    }

    const close = () => closeMarkdownSlashMenu(editor);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [editor, slashMenuState.active]);

  if (
    !editor ||
    !slashMenuState.active ||
    !slashMenuState.range ||
    filteredCommands.length === 0 ||
    !position
  ) {
    return null;
  }

  const commandRange = slashMenuState.range;

  return createPortal(
    <Box
      ref={menuRef}
      className="markdown-editor-slash-menu"
      role="toolbar"
      aria-label="Markdown slash menu"
      position="fixed"
      zIndex={100}
      left={`${position.left}px`}
      top={`${position.top}px`}
      minW={`${MARKDOWN_SLASH_MENU_WIDTH}px`}
      maxW={`min(${MARKDOWN_SLASH_MENU_WIDTH}px, calc(100vw - 16px))`}
      maxH={`min(${MARKDOWN_SLASH_MENU_MAX_HEIGHT}px, calc(100vh - 16px))`}
      overflowY="auto"
    >
      <Flex
        className="markdown-editor-slash-menu__toolbar"
        wrap="wrap"
        align="flex-start"
      >
        {filteredCommands.map((command, index) => {
          const Icon = command.icon;

          return (
            <Button
              key={command.id}
              className="markdown-editor-slash-menu__button"
              data-active={
                index === slashMenuState.selectedIndex ? 'true' : 'false'
              }
              variant="ghost"
              w="11"
              h="11"
              p="0"
              justifyContent="center"
              alignItems="center"
              fontWeight="normal"
              title={command.label}
              aria-label={command.label}
              onMouseEnter={() =>
                setMarkdownSlashMenuSelectedIndex(editor, index)
              }
              onMouseDown={(event) => {
                event.preventDefault();
              }}
              onClick={() =>
                runMarkdownSlashCommand(editor, command, commandRange)
              }
            >
              <Icon aria-hidden size={18} />
            </Button>
          );
        })}
      </Flex>
    </Box>,
    document.body,
  );
}
